import React, { useState } from "react";
import { Box, Typography, CircularProgress, Alert, Tabs, Tab, alpha, Select, MenuItem } from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Insights as InsightsIcon,
  History as HistoryIcon,
  Groups as GroupsIcon,
  MailOutline as MailIcon,
} from "@mui/icons-material";
import { useTournamentStats } from "../hooks/useTournamentStats";
import { useOrganization } from "../contexts/OrganizationContext";
import { getOrgGradient } from "../utils/orgGradient";
import InvitationManager from "./InvitationManager";
import TournamentAggregateStats from "./TournamentAggregateStats";
import TournamentPointsTrend from "./TournamentPointsTrend";
import TournamentPartnerStats from "./TournamentPartnerStats";
import TournamentHistoryChart from "./TournamentHistoryChart";
import TournamentHeadToHeadCard from "./TournamentHeadToHeadCard";

interface TournamentPerformanceSectionProps {
  playerId: string;
}

const TABS = [
  { label: "Overview", icon: <InsightsIcon fontSize="small" /> },
  { label: "Match History", icon: <HistoryIcon fontSize="small" /> },
  { label: "Partners & Rivals", icon: <GroupsIcon fontSize="small" /> },
  { label: "Invitation History", icon: <MailIcon fontSize="small" /> },
];

const TournamentPerformanceSection: React.FC<TournamentPerformanceSectionProps> = ({
  playerId,
}) => {
  const { data, loading, error } = useTournamentStats(playerId);
  const { currentOrg } = useOrganization();
  const gradient = getOrgGradient(currentOrg?.slug);
  const isPE = currentOrg?.slug === "padel-eagle";
  const [tab, setTab] = useState(0);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error || "Failed to load tournament stats"}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Hero header */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3,
          borderRadius: isPE ? "16px" : 3,
          background: isPE ? "#743444" : gradient,
          color: "white",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isPE ? "none" : 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isPE ? "#E49421" : alpha("#fff", 0.18),
            }}
          >
            <TrophyIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1.2, color: "#fff" }}>
              Tournament Performance
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Your career across every completed tournament
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: { xs: 2, sm: 4 } }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
              {data.aggregate.tournamentsPlayed}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Tournaments
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
              {Math.round(data.aggregate.winRate * 100)}%
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Win Rate
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1 }}>
              {data.aggregate.totalMatches}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Matches
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Narrow screens: a dropdown so no tab gets cut off */}
      <Select
        value={tab}
        onChange={(e) => setTab(Number(e.target.value))}
        fullWidth
        size="small"
        sx={{ display: { xs: "flex", sm: "none" }, mb: 3 }}
        MenuProps={{ disableScrollLock: true }}
      >
        {TABS.map((t, i) => (
          <MenuItem key={t.label} value={i} sx={{ gap: 1, fontWeight: 600 }}>
            {t.icon}
            {t.label}
          </MenuItem>
        ))}
      </Select>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          display: { xs: "none", sm: "flex" },
          mb: 3,
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            mr: 1,
          },
          "& .MuiTabs-indicator": { display: "none" },
          "& .Mui-selected": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        }}
      >
        {TABS.map((t) => (
          <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} />
        ))}
      </Tabs>

      <Box role="tabpanel" hidden={tab !== 0}>
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TournamentAggregateStats aggregate={data.aggregate} />
            <TournamentPointsTrend pointsTrend={data.pointsTrend} />
          </Box>
        )}
      </Box>

      <Box role="tabpanel" hidden={tab !== 1}>
        {tab === 1 && <TournamentHistoryChart playerId={playerId} />}
      </Box>

      <Box role="tabpanel" hidden={tab !== 2}>
        {tab === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TournamentPartnerStats partners={data.partners} bestPartner={data.bestPartner} />
            <TournamentHeadToHeadCard playerId={playerId} opponents={data.opponents} />
          </Box>
        )}
      </Box>

      <Box role="tabpanel" hidden={tab !== 3}>
        {tab === 3 && <InvitationManager historyOnly />}
      </Box>
    </Box>
  );
};

export default TournamentPerformanceSection;
